// src/components/PaymentMethodTable.jsx
import React, { useMemo } from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { IconButton, Tooltip, Box, Chip, Switch, Stack, Typography } from "@mui/material";
import { useCompanies } from "../hooks/useCompanies";

/** Rótulo do tipo (mesmo mapping do admin) */
const tipoLabel = (t) => {
  const n = Number(t);
  if (n === 1) return "PIX Buspay";
  if (n === 2) return "Pagar.me";
  if (n === 3) return "Cielo";
  if (n === 4) return "Pague Fácil";
  if (n === 5) return "Dinheiro";
  if (n === 6) return "Mercado Pago";
  if (n === 7) return "Xpay";
  if (n === 8) return "PIX Itaú";
  if (n === 9) return "Rede";
  if (n === 10) return "Adyen";
  if (n === 11) return "AppMax (link)";
  if (n === 12) return "AppMax (PIX)";
  if (n === 13) return "AppMax (cartão)";
  return "Default";
};

/**
 * Tabela de Formas de Pagamento
 * Mostra:
 *  - Nome + chips (Default, Emite BPe)
 *  - Status (Switch inline)
 *  - Alcance (Todos os projetos | Nome do Projeto (ID X))
 *  - Ações (Editar/Excluir)
 */
const PaymentMethodTable = ({
  paymentMethods = [],
  rowCount = 0,
  pageSize,
  page,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onEditMethod,
  onDeleteMethod,
  onDeleteSelected,
  onToggleStatus,
  isLoading,
}) => {
  // Carrega empresas/projetos para mapear id_projeto -> nome
  const { data: compData } = useCompanies(1, 500, "");
  const companies = compData?.companies || [];

  const projectMap = useMemo(() => {
    const map = new Map();
    for (const c of companies) {
      const name = c?.nome || c?.fantasia || c?.razao_social || `Projeto #${c?.id}`;
      map.set(Number(c.id), `${name} (ID ${c.id})`);
    }
    return map;
  }, [companies]);

  const projectLabel = (idProjeto) => {
    if (idProjeto == null) return "Todos os projetos";
    const label = projectMap.get(Number(idProjeto));
    return label || `Projeto ID ${idProjeto}`;
  };

  const rows = useMemo(
    () =>
      paymentMethods.map((m) => ({
        ...m,
        id: m.id,
        nome: m.nome || "N/A",
        tipo_label: tipoLabel(m.tipo),
        status_label: Number(m.status) === 1 ? "Ativo" : "Inativo",
        default_label: Number(m.default) === 1 ? "Default" : "",
        alcance_label: projectLabel(m.id_projeto),
      })),
    [paymentMethods, projectMap]
  );

  const columns = [
    {
      field: "nome",
      headerName: "Forma",
      width: 380,
      sortable: false,
      renderCell: (p) => (
        <Stack spacing={0.3} sx={{ minWidth: 0 }}>
          <Typography fontWeight={600} noWrap title={p.row.nome}>
            {p.row.nome}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap title={p.row.tipo_label}>
              {p.row.tipo_label}
            </Typography>
            {Number(p.row.default) === 1 && (
              <Chip size="small" label="Default" color="primary" variant="outlined" />
            )}
            {Number(p.row.emitirbpe) === 1 && (
              <Chip size="small" label="Emite BPe" variant="outlined" />
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "alcance_label",
      headerName: "Alcance",
      width: 260,
      sortable: false,
      valueGetter: (p) => p?.row?.alcance_label ?? "—",
      renderCell: (p) => (
        <Typography variant="body2" noWrap title={p.row.alcance_label}>
          {p.row.alcance_label}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Switch
            size="small"
            checked={Number(params.row.status) === 1}
            onChange={() => onToggleStatus?.(params.row)}
          />
          <Chip
            size="small"
            label={params.row.status_label}
            color={Number(params.row.status) === 1 ? "success" : "default"}
            variant="outlined"
          />
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => onEditMethod?.(params.row)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => onDeleteMethod?.(params.row.id)}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      onExportPDFTitle="Formas de Pagamento Selecionadas"
      exportFileName="metodos_pagamento"
      onDeleteSelected={onDeleteSelected}
      searchPlaceholder="Buscar por nome…"
      pageSizeOptions={[10, 25, 50, 100]}
      dense
    />
  );
};

export default PaymentMethodTable;
